// Property Tycoon — game data
// Same four-phase shape as the other levels: Recon (tag real listings) ->
// Planning (choose a path and commit to real numbers) -> Campaign (defend
// that path through a handful of realistic encounters) -> Debrief (real
// math + save). This level branches into three distinct investment paths
// after Planning, mirroring the source material's three strategies.
//
// All prices, rates, and rents are simplified, illustrative figures for
// teaching purposes — not financial advice, and not a promise of real-world
// results.

// ---------------------------------------------------------------------------
// STAGE 1 — RECON: six real-ish listings spanning all three strategies. Two
// universal tags apply to every listing regardless of type: how it'd likely
// be financed, and whether it's a red flag worth walking away from.
// ---------------------------------------------------------------------------
export const FINANCING_OPTIONS = [
  { id: "LOW_DOWN", label: "Low Down (FHA)" },
  { id: "STANDARD_DOWN", label: "Standard Down" },
  { id: "CASH_CREATIVE", label: "Cash / Creative" },
];

export const RECON_LISTINGS = [
  {
    id: "condo",
    title: "Downtown Condo, 2BR",
    description: "Asking $210,000. HOA $450/mo. Rental leases restricted to a 6-month minimum.",
    correctFinancing: "STANDARD_DOWN",
    correctRedFlag: true,
    note: "High HOA dues and rental restrictions are exactly why condos are usually a pass when starting out.",
  },
  {
    id: "duplex",
    title: "Owner-Occupant Duplex",
    description: "Asking $260,000. FHA available, 3.5% down. The other unit rents for $1,400/mo — your side costs about $300/mo after that income.",
    correctFinancing: "LOW_DOWN",
    correctRedFlag: false,
    note: "Textbook house hack: an owner-occupied loan at 3.5% down, with a tenant covering most of the mortgage.",
  },
  {
    id: "fourplex",
    title: "Fourplex, Needs Work",
    description: "Asking $480,000. Needs a full roof replacement (~$40,000) and new HVAC (~$15,000) across units. Seller won't credit repairs.",
    correctFinancing: "STANDARD_DOWN",
    correctRedFlag: true,
    note: "Major structural and system repairs are exactly what beginners are told to avoid.",
  },
  {
    id: "overpriced-lot",
    title: "Rural 2-Acre Lot",
    description: "Owned free and clear for 15 years. Asking $22,000 — comparable recent sales average $21,000.",
    correctFinancing: "CASH_CREATIVE",
    correctRedFlag: true,
    note: "Priced right at market value — not a motivated seller. Land deals only work at a steep discount.",
  },
  {
    id: "sfh-rental",
    title: "Single-Family Rental",
    description: "Asking $195,000. Market rent $1,650/mo. Estimated total monthly costs (mortgage, tax, insurance, maintenance): $1,380. Offer includes a professional inspection contingency.",
    correctFinancing: "STANDARD_DOWN",
    correctRedFlag: false,
    note: "Cash-flow positive, and the offer is contingent on inspection — exactly the checklist to follow.",
  },
  {
    id: "motivated-lot",
    title: "Vacant Lot, Price Just Dropped",
    description: "Listed 6 months ago for $30,000 (comps around $29,000). Relisted last week at $9,500, right after the property tax bill came due. No liens, no debt.",
    correctFinancing: "CASH_CREATIVE",
    correctRedFlag: false,
    note: "A motivated seller, a tax-driven deadline, and a price already near the 25–35%-of-value range — this is the profile land flippers look for.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — PLANNING: three paths. Land reuses the motivated-seller lot from
// Recon; Rental reuses the SFH; House Hack reuses the duplex — so the
// numbers a player just evaluated become the numbers they commit to.
// ---------------------------------------------------------------------------
export const PATHS = [
  { id: "land", label: "Land Flip", icon: "🏞️", accent: "lime", pitch: "Buy overlooked land far under value, then resell it or owner-finance it." },
  { id: "rental", label: "Rental Property", icon: "🏠", accent: "cyan", pitch: "Buy a cash-flowing rental and collect rent every month." },
  { id: "househack", label: "House Hack", icon: "🏡", accent: "magenta", pitch: "Buy a multi-unit, live in one side, let a tenant cover most of the mortgage." },
];

export const LAND_DEAL = {
  marketValue: 29000,
  askingPrice: 9500,
  offerMin: Math.round(29000 * 0.25), // 7,250
  offerMax: Math.round(29000 * 0.35), // 10,150
  financingTermYears: 5,
};

export const RENTAL_PROPERTY = {
  price: 195000,
  marketRent: 1650,
  fixedMonthlyCosts: 480, // taxes + insurance + maintenance, combined estimate
};

export const HOUSEHACK_PROPERTY = {
  price: 240000,
  otherUnitRent: 1400,
  fixedMonthlyCosts: 350,
  comparisonRent: 1200, // what a similar unit would cost to rent elsewhere
  downPaymentPct: 3.5,
  rate: 6.5,
};

export const CREDIT_TIERS = [
  { id: "fair", label: "620–659", rate: 7.5 },
  { id: "good", label: "660–699", rate: 6.8 },
  { id: "great", label: "700–730+", rate: 6.0 },
];

export const DOWN_PAYMENT_OPTIONS = [15, 20];

// ---------------------------------------------------------------------------
// STAGE 3 — CAMPAIGN: four encounters per path. Every `effect` uses the same
// sign convention: monthlyDelta is the change to monthly cash position
// (negative = costs money), oneTimeCost is a positive dollar amount
// subtracted once in the Debrief math.
// ---------------------------------------------------------------------------
export const LAND_ENCOUNTERS = [
  {
    id: "l1",
    label: "Seller Negotiation",
    text: "You offer within the 25–35%-of-value range. The seller counters at $15,000 — well above what the math supports.",
    choices: [
      { id: "hold", label: "Hold firm, explain the numbers, offer a small bump as your final", tag: "good", result: "The seller took the tax-bill math seriously and accepted your firm counter.", effect: { forceFinalCost: null } },
      { id: "cave", label: "Just pay the $15,000 to close the deal fast", tag: "bad", result: "The deal closed, but most of the margin went with it.", effect: { forceFinalCost: 15000 } },
    ],
  },
  {
    id: "l2",
    label: "Follow-Up Discipline",
    text: "Your first mailer to a similar seller got no response.",
    choices: [
      { id: "follow-up", label: "Follow up again in 30 days — most deals take repeat touches", tag: "good", result: "Persistence paid off, as it usually does with land sellers.", effect: {} },
      { id: "give-up", label: "Give up after one try", tag: "bad", result: "That's real marketing spend with nothing to show for it.", effect: { oneTimeCost: 500 } },
    ],
  },
  {
    id: "l3",
    label: "Due Diligence",
    text: "Before closing, you can check county GIS records for road access and flood zone status.",
    choices: [
      { id: "check", label: "Verify access and flood zone before closing", tag: "good", result: "All clear — the lot is exactly as advertised.", effect: {} },
      { id: "skip", label: "Skip it to save time", tag: "bad", result: "Turns out the parcel has no legal road access. That's an expensive surprise.", effect: { oneTimeCost: 1500 } },
    ],
  },
  {
    id: "l4",
    label: "Exit Strategy",
    text: "Time to decide how this deal pays off.",
    choices: [
      { id: "cash", label: "Sell for cash now — one lump payout", tag: "good", result: "Quick and simple: cash in hand.", effect: { exitStrategy: "cash" } },
      { id: "financing", label: "Offer owner financing — smaller upfront, paid monthly over time", tag: "good", result: "Slower, but it turns one lot into years of passive income.", effect: { exitStrategy: "financing" } },
    ],
  },
];

export const RENTAL_ENCOUNTERS = [
  {
    id: "r1",
    label: "Lender Conversation",
    text: "Time to talk financing.",
    choices: [
      { id: "soft-quote", label: "Get a soft-pull, pre-approval quote first", tag: "good", result: "Your credit stayed untouched while you compared offers.", effect: {} },
      { id: "hard-pulls", label: "Apply at five banks the same week", tag: "bad", result: "Five hard inquiries nudged your rate up a notch.", effect: { rateBump: 0.5 } },
    ],
  },
  {
    id: "r2",
    label: "The Inspection",
    text: "The inspector finds a repairable plumbing issue.",
    choices: [
      { id: "negotiate", label: "Negotiate a credit or repair before closing", tag: "good", result: "The seller covered it. No surprise bill later.", effect: {} },
      { id: "ignore", label: "Ignore it and close anyway", tag: "bad", result: "It failed for real three months in. That inspection contingency exists for a reason.", effect: { oneTimeCost: 1800 } },
    ],
  },
  {
    id: "r3",
    label: "First Tenant",
    text: "You've got an application in hand.",
    choices: [
      { id: "screen", label: "Screen thoroughly — credit, income, references", tag: "good", result: "A reliable tenant, on time every month.", effect: {} },
      { id: "rush", label: "Rent to the first applicant to fill it fast", tag: "bad", result: "Late payments and turnover ate into the numbers all year.", effect: { monthlyDelta: -150 } },
    ],
  },
  {
    id: "r4",
    label: "Maintenance Call",
    text: "A toilet breaks late on a Friday night.",
    choices: [
      { id: "respond", label: "Handle it promptly — call your plumber", tag: "good", result: "Handled by Saturday. A tenant who trusts you renews their lease.", effect: {} },
      { id: "ignore-tenant", label: "Let it wait a week", tag: "bad", result: "The tenant left a scathing review and didn't renew.", effect: { monthlyDelta: -75 } },
    ],
  },
];

export const HOUSEHACK_ENCOUNTERS = [
  {
    id: "h1",
    label: "Roommate Dynamics",
    text: "You're about to share a building with a paying tenant.",
    choices: [
      { id: "clear-rules", label: "Set clear written house rules and lease terms up front", tag: "good", result: "No confusion later — everyone knows the deal.", effect: {} },
      { id: "casual", label: "Keep it casual, no lease specifics", tag: "bad", result: "A dispute over noise and guests turned expensive to sort out.", effect: { monthlyDelta: -100 } },
    ],
  },
  {
    id: "h2",
    label: "The One-Year Rule",
    text: "Four months in, a great job offer shows up in another city. FHA loans require living in the property for at least a year.",
    choices: [
      { id: "stay", label: "Stay to fulfill the occupancy requirement, plan the move for after year one", tag: "good", result: "The financing stayed clean, and the job offer waited.", effect: {} },
      { id: "move", label: "Move immediately anyway", tag: "bad", result: "That's a real compliance problem with an owner-occupied loan, and it wasn't cheap to untangle.", effect: { oneTimeCost: 3000 } },
    ],
  },
  {
    id: "h3",
    label: "Tax Season",
    text: "Your first tax season as a part-time landlord.",
    choices: [
      { id: "claim", label: "Work with a preparer to claim depreciation and expenses properly", tag: "good", result: "Real savings, claimed correctly.", effect: {} },
      { id: "skip-deductions", label: "Skip claiming anything, too much hassle", tag: "bad", result: "Money left on the table that was legally yours.", effect: { oneTimeCost: 1200 } },
    ],
  },
  {
    id: "h4",
    label: "Privacy Trade-off",
    text: "Shared walls mean shared friction sometimes.",
    choices: [
      { id: "boundaries", label: "Set shared-space boundaries early and kindly", tag: "good", result: "A little awkward conversation now, a lot smoother living later.", effect: {} },
      { id: "avoid", label: "Let small issues slide to avoid awkwardness", tag: "bad", result: "Small issues piled into a genuinely stressful living situation.", effect: { monthlyDelta: -50 } },
    ],
  },
];

export const ENCOUNTERS_BY_PATH = {
  land: LAND_ENCOUNTERS,
  rental: RENTAL_ENCOUNTERS,
  househack: HOUSEHACK_ENCOUNTERS,
};

// ---------------------------------------------------------------------------
// MATH HELPERS
// ---------------------------------------------------------------------------

// Standard monthly mortgage payment for a fully amortizing fixed-rate loan.
export function mortgagePayment(loanAmount, annualRatePct, years = 30) {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return loanAmount / n;
  return (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export function sumEffects(log, field) {
  return log.reduce((total, entry) => total + (entry.choice.effect?.[field] || 0), 0);
}
